<template>
    <div>
        <LoadingText v-if="!files"/>
        <div :class="{loading: working}" class="files" v-else>
            <div @click="showListOptions = !showListOptions" class="page-btn enabled sort-btn">
                <span style="text-transform: capitalize">{{query.orderVariable}}</span>
                <Icon :name="query.orderType === 'asc' ? 'arrow_upward' : 'arrow_downward'"/>
                <span style="margin-left: auto" v-if="query.filterType">Only {{query.filterType}}</span>
                <div class="change-list-options-layout" v-if="showListOptions">
                    <section>
                        <p>Sort by:</p>
                        <div class="options">
                            <span :class="{selected: query.orderVariable === item.value}"
                                  :key="item.value"
                                  @click="changeQuery('orderVariable', item.value)"
                                  v-for="item in sortByOptions">
                                {{item.title}}
                            </span>
                        </div>
                    </section>
                    <section>
                        <p>Sort direction:</p>
                        <div class="options">
                            <span :class="{selected: query.orderType === item.value}"
                                  :key="item.value"
                                  @click="changeQuery('orderType', item.value)"
                                  v-for="item in sortByTypes">
                                {{item.title}}
                            </span>
                        </div>
                    </section>
                    <section>
                        <p>Filter type:</p>
                        <div class="options">
                            <span :class="{selected: query.filterType === item}"
                                  :key="item || 'any'"
                                  @click="changeQuery('filterType', item)"
                                  v-for="item in typeFilterValues">
                                {{item || 'any'}}
                            </span>
                        </div>
                    </section>
                </div>
            </div>

            <FileListEntry :file="file" class="file" v-for="file in files"/>


            <div class="page-buttons" v-if="query.canGoForward || query.canGoBackward">
                <p :class="{enabled: query.canGoBackward}" @click="previousPage" class="page-btn">
                    <Icon name="navigate_before"/>
                    <span>Previous page</span>
                </p>
                <p :class="{enabled: query.canGoForward}" @click="nextPage" class="page-btn">
                    <span>Next page</span>
                    <Icon name="navigate_next"/>
                </p>
            </div>
        </div>
    </div>
</template>

<script>
    import * as firebase from 'firebase/app';
    import 'firebase/firestore';
    import {requireUser} from '../other/page-global';
    import FileListEntry from '../components/FileListEntry';
    import LoadingText from '../components/LoadingText';
    import NiceButton from '../components/NiceButton';
    import Icon from '../components/Icon';

    const sortByOptions = [
        {value: 'name', title: 'Name'},
        {value: 'size', title: 'Size'},
        {value: 'created', title: 'Creation time'},
    ];
    const sortByTypes = [
        {value: 'asc', title: 'Ascending'},
        {value: 'desc', title: 'Descending'},
    ];
    const typeFilterValues = [null, 'image', 'text', 'music', 'video', 'other'];

    export default {
        name: 'FilesListLayout',
        components: {Icon, NiceButton, LoadingText, FileListEntry},
        methods: {
            changeQuery(key, value) {
                if (this.query[key] === value) return;
                this.query[key] = value;
                this.rebuildBaseQuery();
                this.firstPage();
            },
            onGotFiles({empty, docs, size}) {
                this.working = false;
                this.isEmpty = empty;
                this.files = docs.map(e => ({...e.data(), id: e.id}));
                this.first = docs[0];
                this.last = docs[this.files.length - 1];

                if (empty && this.query.lastType !== 'none')
                    this.firstPage();

                this.query.canGoForward = size === this.query.limit;
                this.query.canGoBackward = this.query.lastType !== 'none';

                for (let i = 0; i < 0; i++)
                    this.files?.push(...this.files);
            },
            nextPage() {
                if (this.working) return;

                if (!this.last) return;

                if (this.unsub) {
                    this.unsub();
                    this.unsub = null;
                }
                this.query.lastType = 'start';

                this.working = true;
                this.unsub = this.query.base
                                 .startAfter(this.last)
                                 .onSnapshot(this.onGotFiles);
            },
            previousPage() {
                if (this.working) return;

                if (!this.first) return;

                if (this.unsub) {
                    this.unsub();
                    this.unsub = null;
                }

                this.query.lastType = 'end';

                this.working = true;
                this.unsub = this.query.base
                                 .endBefore(this.first)
                                 .limitToLast(this.query.limit)
                                 .onSnapshot(this.onGotFiles);
            },
            firstPage() {
                if (this.working) return;
                if (this.unsub) {
                    this.unsub();
                    this.unsub = null;
                }
                this.query.lastType = 'none';
                this.working = true;
                this.unsub = this.query.base.onSnapshot(this.onGotFiles);
            },
            rebuildBaseQuery() {
                let query = this.query.ref;
                if (this.query.orderVariable)
                    query = query.orderBy(this.query.orderVariable, this.query.orderType);
                if (this.query.filterType)
                    query = query.where('type', '==', this.query.filterType);

                this.query.base = query;
            }
        },
        created() {
            requireUser(this)
                .then(user => {
                    const uid = user.uid;
                    this.query.ref = firebase
                        .firestore()
                        .collection('files')
                        .where('uid', '==', uid)
                        .limit(this.query.limit);

                    this.rebuildBaseQuery();

                    this.firstPage();
                });
        },
        destroyed() {
            if (this.unsub)
                this.unsub();
            this.unsub = null;
        },
        data() {
            return {
                sortByOptions,
                sortByTypes,
                typeFilterValues,
                unsub: null,
                working: false,
                isEmpty: false,
                files: null,
                showListOptions: false,
                query: {
                    lastType: null,
                    canGoForward: false,
                    canGoBackward: false,
                    limit: 10,
                    ref: null,
                    base: null,
                    filterType: null,
                    orderVariable: 'created',
                    orderType: 'desc'
                },
            };
        }
    };
</script>

<!--suppress CssUnusedSymbol -->
<style scoped>
    .files {
        border-radius: 6px;
        margin: 4px 0;
        animation: FadeIn .3s;
        transition: opacity .3s;
    }

    .loading .file {
        opacity: .2;
        pointer-events: none;
    }

    .files > *, .page-btn {
        transition: background-color .2s;
    }

    .files > *:hover, .page-btn:hover {
        background-color: #ffffff11;
    }

    .files > *:last-child {
        border-bottom: none;
    }

    .page-buttons {
        display: flex;
        flex-direction: row;
    }

    .page-buttons:hover {
        background-color: initial;
    }

    .page-btn {
        flex: 1;
        color: #ffffff77;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 16px;
        text-decoration: none;
        pointer-events: none;
    }

    .page-btn.enabled {
        color: #ffffffcc;
        cursor: pointer;
        pointer-events: unset;
    }

    .sort-btn {
        justify-content: left;
        position: relative;
    }

    .sort-btn > .material-icons {
        font-size: 1em;
        padding: 4px;
    }

    .change-list-options-layout {
        animation: DialogOpenAnimation .3s;
        z-index: 40;
        position: absolute;
        max-width: 80vw;
        top: 6px;
        left: 12px;
        background-color: #38388b;
        border-radius: 8px;
        box-shadow: 0 4px 6px 0 rgba(0, 0, 0, .7);
    }

    .change-list-options-layout > section {
        border-bottom: 1px solid #aaa;
        padding: 6px;
    }

    .change-list-options-layout > section:last-child {
        border-bottom: none;
    }

    .change-list-options-layout .options {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: left;
        align-items: center;
    }

    .change-list-options-layout p {
        font-weight: bold;
        text-transform: uppercase;
        text-align: left;
        margin: 4px 8px;
    }

    .change-list-options-layout span {
        background-color: white;
        border-radius: 12px;
        margin: 4px;
        padding: 4px 6px;
        font-size: .9em;
        font-weight: 600;
        color: #444;
        transition: color .3s, background-color .3s;
    }

    .change-list-options-layout .selected {
        background-color: #389a70;
        color: white;
        box-shadow: 0 1px 4px 0 rgba(0, 0, 0, .5);
    }
</style>
